'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback } from 'react'
import { Button } from '@/components/button'
import { T } from '@/components/t'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { Locale } from '@/setup/locales'
import { getShareableUrl, UrlConfig } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'
import { buildRestaurantDeeplink } from '@/utils/url/deeplinks'

interface IRestaurantDetailMobileCtaProps {
    className?: string
}

export const RestaurantDetailMobileCta = ({ className }: IRestaurantDetailMobileCtaProps) => {
    const { citySlug, restaurantSlug } = useRestaurantDetailContext()

    const _t = useTranslations()

    const locale = useLocale()

    const onShareClick = useCallback(() => {
        if (navigator.share) {
            const currentOgTitle = document
                .querySelector('meta[property="og:title"]')
                ?.getAttribute('content')

            const currentTitle = document.title

            const restaurantUrl = UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug,
                restaurantSlug,
            })

            const shareableUrl = getShareableUrl(restaurantUrl, locale as Locale)

            navigator
                .share({
                    title: currentOgTitle ?? currentTitle ?? 'NeoTaste',
                    url: shareableUrl,
                })
                .catch((e) => {
                    // Navigatior.share sees user canceling as an error
                    // biome-ignore lint/suspicious/noConsole: intentional error logging
                    console.log('Did not share', e)
                })
        }
    }, [citySlug, restaurantSlug, locale])

    const restaurantDeeplink = buildRestaurantDeeplink(restaurantSlug)

    return (
        <div
            className={cn(
                // Bleeds into the page gutters so scrolling buttons reach the screen
                // edge instead of clipping at the container edge
                'md:hidden flex gap-2 overflow-x-auto scrollbar-hide -mx-4 w-[calc(100%+32px)] px-4',
                className
            )}
        >
            <a href={restaurantDeeplink} className="shrink-0">
                <Button
                    styleType="secondary"
                    size="small"
                    className="whitespace-nowrap"
                    onClick={() => {}}
                >
                    <Image
                        src="/_images/share/neotaste-icon-logo.png"
                        alt="NeoTaste"
                        width={16}
                        height={16}
                        className="shrink-0 rounded-s"
                    />
                    <T t="restaurants.detail.cta.openInApp" />
                </Button>
            </a>

            <Button
                onClick={onShareClick}
                styleType="secondary"
                size="small"
                className="whitespace-nowrap shrink-0"
            >
                <T t="restaurants.detail.cta.share" />
            </Button>
        </div>
    )
}
